import { useEffect, useRef } from 'react';
import type { SelectedFile } from '@witmetrics/utils';
import { isImage, isPdf, isScan, isVideo } from '@witmetrics/utils/files';
import { useToggle } from '@/hooks/useToggle';
import { centerObjects, loadScan } from '@/utils/scans';
import SceneProvider, { useScene } from '@/providers/SceneProvider';
import Dialog, { DialogBody, DialogTitle } from '@/components/Dialog';
import LoopingEllipsis from '@/components/LoopingEllipsis';
import Scene from '@/components/Scene';
import ImageViewer from '@/components/viewers/ImageViewer';
import PdfViewer from '@/components/viewers/PdfViewer';
import VideoViewer from '@/components/viewers/VideoViewer';
import { classes } from './utils';

export type FilePreviewDialogProps = {
  selectedFile: SelectedFile;
  onClose: () => void;
};

export default function FilePreviewDialog({
  selectedFile,
  onClose,
}: FilePreviewDialogProps) {
  return (
    <Dialog className={classes.dialog} onClose={onClose}>
      <DialogTitle
        className={classes.title}
        title={selectedFile.file.name}
        onClose={onClose}
      />
      <DialogBody className={classes.body}>
        <SceneProvider>
          <PreviewContent selectedFile={selectedFile} />
        </SceneProvider>
      </DialogBody>
    </Dialog>
  );
}

type PreviewContentProps = {
  selectedFile: SelectedFile;
};

function PreviewContent({ selectedFile }: PreviewContentProps) {
  const objectNameRef = useRef<string | null>(null);
  const cancelledRef = useRef(false);
  const { addToScene, clearScene, getScene, zoomToObjects } = useScene();
  const [isLoading, toggleLoading] = useToggle(isScan(selectedFile.file.name));

  useEffect(() => {
    cancelledRef.current = false;
    if (isScan(selectedFile.file.name)) loadScanFile();
    return () => {
      cancelledRef.current = true;
      clearScene();
    };
  }, [selectedFile.uuid]);

  const loadScanFile = async () => {
    try {
      const buffer = await selectedFile.file.arrayBuffer();
      if (cancelledRef.current) return;
      const mesh = loadScan(selectedFile.file.name, buffer);
      if (!mesh) return toggleLoading(false);
      const group = centerObjects([mesh]);
      group.name = `pending_${selectedFile.uuid}`;
      objectNameRef.current = group.name;
      clearScene();
      addToScene([group]);
      toggleLoading(false);
    } catch {
      toggleLoading(false);
    }
  };

  const handleMount = () => {
    if (!objectNameRef.current) return;
    const group = getScene().getObjectByName(objectNameRef.current);
    if (group) zoomToObjects([group]);
  };

  const renderViewer = () => {
    const { file, source } = selectedFile;
    if (isScan(file.name)) {
      return (
        !isLoading && <Scene className={classes.scene} onMount={handleMount} />
      );
    }
    if (isImage(file.name)) {
      return <ImageViewer className={classes.scene} src={source} />;
    }
    if (isPdf(file.name)) {
      return (
        <PdfViewer
          className={classes.pdfViewer}
          controlsClassName={classes.pdfControls}
          src={file}
        />
      );
    }
    if (isVideo(file.name)) {
      return <VideoViewer className={classes.scene} src={source} />;
    }
    return (
      <div className={classes.message}>
        Preview isn&apos;t available for this file type.
      </div>
    );
  };

  return (
    <div className={classes.sceneWrapper}>
      {isLoading && (
        <div className={classes.message}>
          Loading file
          <LoopingEllipsis />
        </div>
      )}
      {renderViewer()}
    </div>
  );
}
